/* eslint-disable max-lines */
import { BN } from '@coral-xyz/anchor'
import { PublicKey } from '@solana/web3.js'
import {
  MOCK_VERIFIER_PROGRAM_ID,
  SETUP_PROGRAM_ID,
  TOKEN_PUB_KEY,
  TREASURY_PROGRAM_ID
} from './addresses'

export const feedWhitelistPda = PublicKey.findProgramAddressSync(
  [Buffer.from('mock-verifier'), Buffer.from('feed-whitelist')],
  MOCK_VERIFIER_PROGRAM_ID
)[0]

export function updownGamePda(pid: PublicKey, gameId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('updown-game'), gameId.toBuffer()],
    pid
  )[0]
}

export function treasuryGamePda(pid: PublicKey, gameId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('game'), gameId.toBuffer()],
    pid
  )[0]
}

export function treasuryTokenConfigPda(
  pid: PublicKey,
  mint: PublicKey
): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('token'), mint.toBuffer()],
    pid
  )[0]
}

export function treasuryVaultAuthorityPda(
  pid: PublicKey,
  mint: PublicKey
): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('vault-authority'), mint.toBuffer()],
    pid
  )[0]
}

export function treasuryConfigPda(pid: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('treasury-config')],
    pid
  )[0]
}

/** PDA under the game program — authorizes CPI from treasury (`distributor`). */
export function distributorPda(gameProgramId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('distributor')],
    gameProgramId
  )[0]
}

/** Bullseye game PDA: seeds ["bull-game", gameId] (see idl/bullseye.json `play.game`). */
export function bullseyeGamePda(pid: PublicKey, gameId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('bull-game'), gameId.toBuffer()],
    pid
  )[0]
}

/** Setup game PDA: seeds ["setup-game", gameIdMarker]. */
export function setupGamePda(
  pid: PublicKey,
  gameIdMarker: PublicKey
): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('setup-game'), gameIdMarker.toBuffer()],
    pid
  )[0]
}

export interface SetupGameIdPdaSeeds {
  programId: PublicKey
  endTime: BN
  takeProfitPrice: BN
  stopLossPrice: BN
  creator: PublicKey
  mint: PublicKey
}

/**
 * Setup game id marker PDA: seeds
 * ["setup-game-id", endTime, takeProfitPrice, stopLossPrice, creator, mint] (u64 args as LE 8 bytes).
 */
export function setupGameIdPda(s: SetupGameIdPdaSeeds): PublicKey {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from('setup-game-id'),
      s.endTime.toArrayLike(Buffer, 'le', 8),
      s.takeProfitPrice.toArrayLike(Buffer, 'le', 8),
      s.stopLossPrice.toArrayLike(Buffer, 'le', 8),
      s.creator.toBuffer(),
      s.mint.toBuffer()
    ],
    s.programId
  )[0]
}

/** Feed whitelist PDA for mock_verifier (same as backend mockVerifierFeedWhitelistPda). */
export function mockVerifierFeedWhitelistPda(
  verifierProgramId: PublicKey
): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('mock-verifier'), Buffer.from('feed-whitelist')],
    verifierProgramId
  )[0]
}

/**
 * When using mock verifier without Chainlink meta, all CPI accounts point to the feed whitelist PDA.
 * Matches backend `resolveVerifierAccounts` mock path.
 */
export function resolveMockVerifierCpis(verifierProgramId: PublicKey): {
  verifierAccount: PublicKey
  accessController: PublicKey
  verifierConfig: PublicKey
  verifierProgramId: PublicKey
  feedWhitelist: PublicKey
} {
  const fw = mockVerifierFeedWhitelistPda(verifierProgramId)
  return {
    verifierAccount: fw,
    accessController: fw,
    verifierConfig: fw,
    verifierProgramId,
    feedWhitelist: fw
  }
}

/** Distributor PDA for Setup program (treasury CPI). */
export function setupDistributorPda(): PublicKey {
  return distributorPda(SETUP_PROGRAM_ID)
}

export const tokenConfigPda = treasuryTokenConfigPda(
  TREASURY_PROGRAM_ID,
  TOKEN_PUB_KEY
)

export function getPlayerDepositPda(
  player: PublicKey,
  mint: PublicKey = TOKEN_PUB_KEY
): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('player'), mint.toBuffer(), player.toBuffer()],
    TREASURY_PROGRAM_ID
  )[0]
}

export interface UpdownAiGameIdPdaSeeds {
  programId: PublicKey
  endTime: BN
  stopPredictAt: BN
  minDeposit: BN
  mint: PublicKey
  nonce: Buffer
}

export function updownAiGameIdPda(s: UpdownAiGameIdPdaSeeds): PublicKey {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from('game-id'),
      s.endTime.toArrayLike(Buffer, 'le', 8),
      s.stopPredictAt.toArrayLike(Buffer, 'le', 8),
      s.minDeposit.toArrayLike(Buffer, 'le', 8),
      s.mint.toBuffer(),
      s.nonce
    ],
    s.programId
  )[0]
}

export function updownAiGamePda(pid: PublicKey, gameId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('updown-ai-game'), gameId.toBuffer()],
    pid
  )[0]
}
